import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface TopBarSlotValue {
  actions: ReactNode;
  setActions: (node: ReactNode) => void;
  leading: ReactNode;
  setLeading: (node: ReactNode) => void;
}

const TopBarSlotContext = createContext<TopBarSlotValue | null>(null);

/**
 * Two slots in the app top bar that a route component can fill with its own
 * controls — without prop drilling or hoisting page state up to the layout.
 * `actions` is the right-hand slot; `leading` is the left-hand slot, which on
 * native root screens (where there is no back button) a page can use for its
 * own navigation. The page keeps its own state and handlers and only registers
 * a React node here; MobileTopBar renders whatever is currently registered.
 */
export const TopBarSlotProvider = ({ children }: { children: ReactNode }) => {
  const [actions, setActions] = useState<ReactNode>(null);
  const [leading, setLeading] = useState<ReactNode>(null);
  const value = useMemo(
    () => ({ actions, setActions, leading, setLeading }),
    [actions, leading],
  );
  return <TopBarSlotContext.Provider value={value}>{children}</TopBarSlotContext.Provider>;
};

/** Read the currently-registered right-hand actions (consumed by MobileTopBar). */
export const useTopBarSlotOutlet = (): ReactNode => useContext(TopBarSlotContext)?.actions ?? null;

/** Read the currently-registered left-hand node (consumed by MobileTopBar). */
export const useTopBarLeadingOutlet = (): ReactNode =>
  useContext(TopBarSlotContext)?.leading ?? null;

/**
 * Register `actions` into the top bar's right slot while `active` is true;
 * clears them when `active` is false or on unmount. Pass a MEMOISED node so
 * this only re-runs when the actions actually change (an unstable node would loop).
 */
export const useTopBarActions = (actions: ReactNode, active = true): void => {
  const setActions = useContext(TopBarSlotContext)?.setActions;
  useEffect(() => {
    if (!setActions) return;
    setActions(active ? actions : null);
    return () => setActions(null);
  }, [setActions, actions, active]);
};

/** Same contract as useTopBarActions, for the left-hand slot. */
export const useTopBarLeading = (leading: ReactNode, active = true): void => {
  const setLeading = useContext(TopBarSlotContext)?.setLeading;
  useEffect(() => {
    if (!setLeading) return;
    setLeading(active ? leading : null);
    return () => setLeading(null);
  }, [setLeading, leading, active]);
};
